import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MdNotifications } from "react-icons/md";
import { useClinic } from "./Context/ClinicaContext";
import { useUser } from "./Context/UserContext";
import { useSafeNavigate } from "../context/TelemedicinaContext";
import { getNotificacoes, markAllAsRead, markAsRead } from "../services/notificationsService";
import { Spinner } from "./index";

const isLegacyFilaNotification = (n) => {
  const t = String(n?.title || "")
    .trim()
    .toLowerCase();
  return (
    n?.tipo !== "fila_espera_ativa" &&
    (t === "fila de espera" || t.startsWith("nova fila"))
  );
};

function filaEsperaResumoItens(dados) {
  const c =
    dados?.pacientesCount != null
      ? Number(dados.pacientesCount)
      : dados?.slotCount != null
        ? Number(dados.slotCount)
        : null;
  if (c === 1) return "Há 1 item em espera.";
  if (c != null && !Number.isNaN(c)) return `Há ${c} itens em espera.`;
  return "Há itens em espera na fila.";
}

const Notificacoes = ({ isOpen = true }) => {
  const navigate = useSafeNavigate();
  const { selectedClinic } = useClinic();
  const { user } = useUser();
  const {
    isOpen: isPopoverOpen,
    onOpen: onPopoverOpen,
    onClose: onPopoverClose,
  } = useDisclosure();

  const userNotifyKey = user
    ? String(user.id ?? user._id ?? user.user?.id ?? user.user?._id ?? "")
    : "";
  const cid = selectedClinic?._id;
  const queryClient = useQueryClient();
  const notificationsQueryKey = ["notifications", userNotifyKey, cid];

  const {
    data: notifData,
    isFetching: notifLoading,
    refetch: refreshNotifications,
  } = useQuery({
    queryKey: notificationsQueryKey,
    queryFn: async () => {
      const { naoLidas } = await getNotificacoes(cid, 10);
      const rawUnread = Array.isArray(naoLidas) ? naoLidas : [];
      const filteredUnread = rawUnread.filter((n) => !isLegacyFilaNotification(n));
      return {
        unreadCount: filteredUnread.length,
        unreadItems: filteredUnread,
      };
    },
    enabled: !!userNotifyKey && !!cid,
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: "always",
    refetchOnMount: true,
    staleTime: 0,
  });

  const unreadCount = notifData?.unreadCount ?? 0;
  const unreadItems = notifData?.unreadItems ?? [];

  const handleNotificationClick = async (n) => {
    // Remove otimisticamente da lista/contador antes mesmo da resposta do servidor.
    queryClient.setQueryData(notificationsQueryKey, (old) => {
      if (!old) return old;
      const nextItems = old.unreadItems.filter((item) => item._id !== n._id);
      return { unreadCount: nextItems.length, unreadItems: nextItems };
    });
    onPopoverClose();
    navigate(`/notificacoes?selected=${n._id}`)
    try {
      await markAsRead(n._id);
    } catch (error) {
      // Se falhar, a próxima busca (refetchInterval/foco) corrige a lista.
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!cid || unreadItems.length === 0) return;
    // Zera otimisticamente antes da resposta do servidor.
    queryClient.setQueryData(notificationsQueryKey, { unreadCount: 0, unreadItems: [] });
    try {
      await markAllAsRead(cid);
    } catch (error) {
      // Se falhar, a próxima busca (refetchInterval/foco) corrige a lista.
    }
  };

  if (!isOpen) return null;

  return (
    <Popover
      placement="bottom-end"
      isOpen={isPopoverOpen}
      onOpen={() => {
        onPopoverOpen();
        refreshNotifications();
      }}
      onClose={onPopoverClose}
    >
      <PopoverTrigger>
        <Box position="relative">
          <Icon
            as={MdNotifications}
            fontSize="24px"
            color="teal.500"
            cursor="pointer"
            aria-label="Notificações"
          />
          {unreadCount > 0 && (
            <Badge
              position="absolute"
              top="-6px"
              right="-8px"
              colorScheme="red"
              borderRadius="full"
              px="2"
              fontSize="xs"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="360px" maxW="calc(100vw - 24px)" h="300px" maxH="calc(100dvh - 24px)" overflowWrap="anywhere" zIndex="popover">
          <PopoverArrow />
          <PopoverHeader fontWeight="semibold">
            <Flex justify="space-between" align="center" gap="2" wrap="wrap">
              <Text fontWeight="semibold">Notificações</Text>
              {unreadItems.length > 0 && (
                <Link fontSize="xs" color="teal.600" onClick={handleMarkAllAsRead}>
                  Marcar todas como lidas
                </Link>
              )}
            </Flex>
          </PopoverHeader>
          <PopoverBody flex="1" minH="0" overflowY="auto">
            {notifLoading ? (
              <HStack justify="center" py="4">
                <Spinner />
                <Text fontSize="sm">Carregando...</Text>
              </HStack>
            ) : unreadItems.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                Nenhuma notificação não lida.
              </Text>
            ) : (
              <VStack align="stretch" spacing="3">
                {unreadItems.slice(0, 10).map((n) =>
                  n.tipo === "fila_espera_ativa" ? (
                    <Box
                      key={n._id}
                      p="2"
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="teal.100"
                      bg="teal.50"
                      cursor="pointer"
                      _hover={{ bg: "teal.100" }}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <Text fontSize="sm" fontWeight="bold" color="teal.800">
                        Fila de espera ativa
                      </Text>
                      <Text fontSize="xs" color="gray.700" mt="1">
                        {filaEsperaResumoItens(n.dados)}
                      </Text>
                      <Text fontSize="xs" color="teal.600" mt="2">
                        Toque para ver detalhes
                      </Text>
                    </Box>
                  ) : n.tipo === "proximo_contato" ? (
                    <Box
                      key={n._id}
                      p="2"
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="blue.100"
                      bg="blue.50"
                      cursor="pointer"
                      _hover={{ bg: "blue.100" }}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <Text fontSize="sm" fontWeight="bold" color="blue.800">
                        Próximos Contatos
                      </Text>
                      <Text fontSize="xs" color="gray.700" mt="1">
                        {n.dados?.contatoCount === 1
                          ? "Há 1 contato agendado para hoje."
                          : `Há ${n.dados?.contatoCount ?? 0} contatos agendados para hoje.`}
                      </Text>
                      <Text fontSize="xs" color="blue.600" mt="2">
                        Toque para ver detalhes
                      </Text>
                    </Box>
                  ) : n.tipo === "atendimento_excedente_tempo" ? (
                    <Box
                      key={n._id}
                      p="2"
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="orange.100"
                      bg="orange.50"
                      cursor="pointer"
                      _hover={{ bg: "orange.100" }}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <Text fontSize="sm" fontWeight="bold" color="orange.800">
                        {n.title || "Atendimentos em atraso"}
                      </Text>
                      <Text fontSize="xs" color="gray.700" mt="1">
                        {Number(n.dados?.total ?? n.dados?.itens?.length ?? 0) || 0}{" "}
                        atendimento(s) — ver lista na página de notificações
                      </Text>
                      <Text fontSize="xs" color="orange.700" mt="2">
                        Toque para ver detalhes
                      </Text>
                    </Box>
                  ) : (
                    <Box
                      key={n._id}
                      p="2"
                      borderWidth="1px"
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                        {n.title}
                      </Text>
                      {n.body ? (
                        <Text fontSize="xs" color="gray.600" noOfLines={2}>
                          {n.body}
                        </Text>
                      ) : null}
                    </Box>
                  ),
                )}
              </VStack>
            )}
          </PopoverBody>
          <PopoverFooter>
            <Flex justify="space-between" align="center" gap="2" wrap="wrap">
              <Link
                fontSize="sm"
                onClick={() => {
                  onPopoverClose();
                  navigate("/notificacoes");
                }}
              >
                Ver todas
              </Link>
              <Button size="sm" variant="ghost" onClick={refreshNotifications}>
                Atualizar
              </Button>
            </Flex>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default Notificacoes;
